import type { Edge, Node, XYPosition } from '@xyflow/react';

// Cards are 280px wide; leave 220px for condition labels and handles.
export const WORKFLOW_STEP_SPACING = 500;

// Reserve space for whole subtrees, not just the immediate children. Otherwise
// children of adjacent parents collide when either branch grows another level.
export function addCenteredBranch(
  sourceId: string, sourcePosition: XYPosition, nodes: Node[], edges: Edge[], newNode: Node,
): Node[] {
  return layoutCenteredBranches(sourceId, sourcePosition, [...nodes, newNode], [
    ...edges, { id: `layout-${newNode.id}`, source: sourceId, target: newNode.id },
  ]);
}

export function layoutCenteredBranches(
  sourceId: string, sourcePosition: XYPosition, allNodes: Node[], links: Edge[],
): Node[] {
  const byId = new Map(allNodes.map(node => [node.id, node]));
  // The source is normally present; the fallback also supports an initial node
  // whose position is supplied before the canvas state has been populated.
  if (!byId.has(sourceId)) {
    byId.set(sourceId, { id: sourceId, position: sourcePosition, data: {} });
  }
  const height = (id: string) => {
    const node = byId.get(id)!;
    return node.measured?.height ?? node.height ?? 140;
  };
  let rootId = sourceId;
  const ancestors = new Set([rootId]);
  while (true) {
    const parent = links.find(edge => edge.target === rootId &&
      byId.has(edge.source) && !ancestors.has(edge.source));
    if (!parent) break;
    rootId = parent.source;
    ancestors.add(rootId);
  }

  // Build a spanning tree: shared targets are placed once, and back edges never
  // recurse indefinitely. The workflow's actual connections stay unchanged.
  const children = new Map<string, string[]>();
  const visited = new Set([rootId]);
  const queue = [rootId];
  for (let index = 0; index < queue.length; index++) {
    const id = queue[index];
    const childIds: string[] = [];
    for (const edge of links) {
      if (edge.source !== id || !byId.has(edge.target) || visited.has(edge.target)) continue;
      visited.add(edge.target);
      childIds.push(edge.target);
      queue.push(edge.target);
    }
    children.set(id, childIds);
  }

  const spans = new Map<string, number>();
  const spacing = new Map<string, number>();
  for (const id of [...queue].reverse()) {
    const childIds = children.get(id)!;
    const childSpan = Math.max(0, ...childIds.map(child => spans.get(child)!));
    spacing.set(id, childSpan + 70);
    spans.set(id, Math.max(height(id), childIds.length * (childSpan + 70) - 70));
  }

  const root = byId.get(rootId)!;
  const positions = new Map<string, XYPosition>([[rootId, root.position]]);
  for (const id of queue) {
    const position = positions.get(id)!;
    const centerY = position.y + height(id) / 2;
    const childIds = children.get(id)!;
    childIds.forEach((child, index) => positions.set(child, {
      x: position.x + WORKFLOW_STEP_SPACING,
      y: centerY + (index - (childIds.length - 1) / 2) * spacing.get(id)! - height(child) / 2,
    }));
  }
  return allNodes.map(node => {
    const position = positions.get(node.id);
    return position && (position.x !== node.position.x || position.y !== node.position.y)
      ? { ...node, position } : node;
  });
}
